/**
 * Jurisdiction table for transaction-friction costs.
 *
 * Each entry specifies the three levers the model needs to compute the total
 * cost of buying and later selling a home in that jurisdiction:
 *
 *   1. buyerClosingCosts     — % of purchase price + fixed component.
 *                              Covers lender/legal/inspection/title fees.
 *   2. transferTaxOnPurchase — land transfer tax / stamp duty, either flat
 *                              zero or a set of marginal-rate brackets.
 *   3. sellerAgentCommission — % of the eventual sale price.
 *
 * Every numeric assumption cites its source inline so a skeptical visitor
 * can audit the model in under a minute (see the "Trust and Transparency"
 * requirements in docs/requrements.md).
 *
 * The rates modelled here are baselines; individual scenarios (Toronto
 * municipal LTT, first-time-buyer rebates, US state transfer taxes) can be
 * overlaid by extending the table.
 */

/** A cost expressed as a percentage of a base amount plus a currency-fixed component. */
export interface FixedPlusPercentFee {
  /** Fraction of the base amount, e.g. 0.02 for 2%. */
  readonly percentOfValue: number;
  /** Fixed component in the jurisdiction's currency. */
  readonly fixed: number;
}

/** A cost expressed purely as a percentage of a base amount. */
export interface FlatPercentFee {
  /** Fraction of the base amount, e.g. 0.06 for 6%. */
  readonly percentOfValue: number;
}

/**
 * One tier of a marginal-rate transfer-tax schedule. `upTo` is the upper
 * bound (inclusive at the boundary; the next tier picks up from there);
 * `null` marks the open-ended top bracket. `marginalRate` applies only to
 * the portion of the price falling within this tier.
 */
export interface TransferTaxBracket {
  readonly upTo: number | null;
  readonly marginalRate: number;
}

/**
 * Land-transfer / stamp-duty schedule. `none` means the jurisdiction has no
 * applicable transfer tax at the modelled level (e.g. no US federal tax);
 * `brackets` is a progressive schedule of marginal rates.
 */
export type TransferTax =
  | { readonly kind: "none" }
  | { readonly kind: "brackets"; readonly brackets: readonly TransferTaxBracket[] };

/** Provenance citation for one line item. Kept as free-form text so it can be surfaced verbatim in tooltips. */
export type Source = string;

export interface Jurisdiction {
  /** Machine-safe stable identifier (URL slug). */
  readonly id: string;
  /** Human-facing label. */
  readonly name: string;
  /** ISO 4217 currency code the numeric values are expressed in. */
  readonly currency: "USD" | "CAD" | "AUD";
  readonly buyerClosingCosts: FixedPlusPercentFee;
  readonly transferTaxOnPurchase: TransferTax;
  readonly sellerAgentCommission: FlatPercentFee;
  readonly sources: {
    readonly buyerClosingCosts: Source;
    readonly transferTaxOnPurchase: Source;
    readonly sellerAgentCommission: Source;
  };
}

/**
 * United States — national baseline.
 *
 * Federal law does not levy a real-estate transfer tax; state/local rates
 * vary from 0% (13 states) to ~2% (Delaware). The national baseline models
 * transfer tax as zero and leaves state-level overlays for a follow-up.
 */
export const US_NATIONAL: Jurisdiction = {
  id: "us-national",
  name: "United States (national baseline)",
  currency: "USD",
  buyerClosingCosts: {
    // Bankrate 2024 buyer closing-cost survey reports a typical 2–5% of
    // purchase price range for the buyer side (lender fees, title
    // insurance, appraisal, inspection, recording, escrow). We use the
    // low end of that range plus a $1,500 fixed component for the
    // inspection/appraisal fees that do not scale with price.
    percentOfValue: 0.02,
    fixed: 1500,
  },
  transferTaxOnPurchase: { kind: "none" },
  sellerAgentCommission: {
    // Pre-March-2024 NAR practice: 6% total commission (typically 3% to
    // each side). Post-settlement commissions are increasingly negotiable
    // but the national headline rate remains the industry starting point.
    percentOfValue: 0.06,
  },
  sources: {
    buyerClosingCosts:
      "Bankrate 2024 closing-costs survey (buyer-side typical range 2–5% of price, excluding transfer taxes).",
    transferTaxOnPurchase:
      "Tax Foundation — no federal real-estate transfer tax; state and local rates vary from 0% to ~2%.",
    sellerAgentCommission:
      "National Association of Realtors historical practice (pre-March-2024 settlement); commissions now negotiable but 6% remains the headline reference rate.",
  },
};

/**
 * Canada — Ontario (excluding Toronto's municipal LTT).
 *
 * Ontario's provincial Land Transfer Tax is a marginal-rate schedule
 * unchanged since 2017. Toronto's municipal LTT mirrors the provincial
 * schedule and doubles the effective rate inside the city — not modelled
 * here; use an overlay for Toronto scenarios.
 */
export const CANADA_ONTARIO: Jurisdiction = {
  id: "ca-on",
  name: "Canada — Ontario",
  currency: "CAD",
  buyerClosingCosts: {
    // OREA/CREA guidance: buyer closing costs (legal, title, inspection,
    // adjustments) typically run 1.5–4% of purchase price. We model the
    // low end plus a $2,000 fixed component covering legal and inspection.
    percentOfValue: 0.015,
    fixed: 2000,
  },
  transferTaxOnPurchase: {
    kind: "brackets",
    brackets: [
      { upTo: 55_000, marginalRate: 0.005 },
      { upTo: 250_000, marginalRate: 0.01 },
      { upTo: 400_000, marginalRate: 0.015 },
      { upTo: 2_000_000, marginalRate: 0.02 },
      { upTo: null, marginalRate: 0.025 },
    ],
  },
  sellerAgentCommission: {
    // CREA / Toronto Real Estate Board convention: 5% total (2.5% each side).
    percentOfValue: 0.05,
  },
  sources: {
    buyerClosingCosts:
      "Ontario Real Estate Association buyer-cost guidance — typical 1.5–4% of purchase price (legal, title, inspection, adjustments).",
    transferTaxOnPurchase:
      "Ontario Ministry of Finance — Land Transfer Tax Act, rates in force 2024–25 (0.5% / 1.0% / 1.5% / 2.0% / 2.5% marginal tiers).",
    sellerAgentCommission:
      "Canadian Real Estate Association / Toronto Real Estate Board — standard 5% total commission (2.5% listing + 2.5% co-operating agent).",
  },
};

/**
 * Australia — New South Wales (residential owner-occupier).
 *
 * NSW Duty of Assessment ("stamp duty") is the marginal-rate schedule in
 * the Duties Act 1997 §32. Threshold amounts are indexed each July;
 * values below are the 1 July 2024 rates. First-home-buyer concessions
 * and the premium-property tier for foreign purchasers are not modelled.
 */
export const AUSTRALIA_NSW: Jurisdiction = {
  id: "au-nsw",
  name: "Australia — New South Wales",
  currency: "AUD",
  buyerClosingCosts: {
    // NSW Fair Trading / typical conveyancer surveys: conveyancing legal
    // work $1,000–$2,000, building & pest $400–$700, lender fees vary.
    // We model 0.5% variable (mostly small lender fees scaling with loan
    // size) plus $2,000 fixed for conveyancing + inspection.
    percentOfValue: 0.005,
    fixed: 2000,
  },
  transferTaxOnPurchase: {
    kind: "brackets",
    brackets: [
      { upTo: 16_000, marginalRate: 0.0125 },
      { upTo: 35_000, marginalRate: 0.015 },
      { upTo: 93_000, marginalRate: 0.0175 },
      { upTo: 351_000, marginalRate: 0.035 },
      { upTo: 1_168_000, marginalRate: 0.045 },
      { upTo: 3_505_000, marginalRate: 0.055 },
      { upTo: null, marginalRate: 0.07 },
    ],
  },
  sellerAgentCommission: {
    // NSW commissions are quoted as a single agent's fee (no split);
    // Ratemyagent's 2024 NSW survey has the median at ~2.2% (typical
    // 2.0–2.5% range, GST-inclusive).
    percentOfValue: 0.022,
  },
  sources: {
    buyerClosingCosts:
      "NSW Fair Trading conveyancing guidance and industry conveyancer surveys — legal work $1,000–$2,000 + building & pest $400–$700 + lender application fees.",
    transferTaxOnPurchase:
      "Revenue NSW — Duties Act 1997 §32 transfer-duty schedule, rates effective 1 July 2024 (1.25% / 1.5% / 1.75% / 3.5% / 4.5% / 5.5% / 7.0% marginal tiers).",
    sellerAgentCommission:
      "Ratemyagent 2024 NSW agent-commission survey — median ~2.2% (typical 2.0–2.5% range, GST-inclusive).",
  },
};

/**
 * Australia — Victoria (residential, general/standard schedule).
 *
 * Victoria's Duties Act 2000 sets a marginal-rate schedule for most of the
 * price range, but the $960,001–$2,000,000 tier is a flat percentage of the
 * ENTIRE dutiable value (not truly marginal). We approximate that band with
 * a 5.5% marginal rate so the schedule can share the TransferTax bracket
 * shape used by every other jurisdiction. The approximation matches the SRO
 * schedule exactly at the $960K and $2M endpoints; between them it can
 * under-estimate by up to ~$130 vs the flat-slab rule. Owner-occupier
 * concessions (PPR up to $550K, first-home-buyer up to $600K/$750K) are not
 * modelled — see docs at sro.vic.gov.au for the concession detail.
 */
export const AUSTRALIA_VIC: Jurisdiction = {
  id: "au-vic",
  name: "Australia — Victoria",
  currency: "AUD",
  buyerClosingCosts: {
    // Consumer Affairs Victoria / conveyancer surveys: conveyancing legal
    // work $1,000–$2,000, building & pest $400–$700, lender application
    // fees vary. Modelled as 0.5% variable (small lender fees scaling with
    // loan size) + $2,000 fixed for conveyancing + inspection — same shape
    // as NSW, since the practical cost structure is essentially identical.
    percentOfValue: 0.005,
    fixed: 2000,
  },
  transferTaxOnPurchase: {
    kind: "brackets",
    brackets: [
      { upTo: 25_000, marginalRate: 0.014 },
      { upTo: 130_000, marginalRate: 0.024 },
      { upTo: 960_000, marginalRate: 0.06 },
      // Real SRO rule: 5.5% of the ENTIRE value in this band. Approximated
      // as a marginal 5.5% here so it slots into the marginal-brackets
      // shape; matches the SRO schedule exactly at $2,000,000.
      { upTo: 2_000_000, marginalRate: 0.055 },
      { upTo: null, marginalRate: 0.065 },
    ],
  },
  sellerAgentCommission: {
    // Ratemyagent / OpenAgent 2025 VIC surveys: metropolitan Melbourne
    // typical 1.6–2.5%, statewide median ~2.0% ex-GST. VIC quotes are
    // conventionally ex-GST; adding 10% GST gives ~2.2% effective — used
    // here so the number is comparable to the other jurisdictions'
    // out-of-pocket cost to the seller.
    percentOfValue: 0.022,
  },
  sources: {
    buyerClosingCosts:
      "Consumer Affairs Victoria conveyancing guidance and industry conveyancer surveys — legal work $1,000–$2,000 + building & pest $400–$700 + lender application fees.",
    transferTaxOnPurchase:
      "State Revenue Office Victoria — Duties Act 2000 general (non-PPR) land-transfer-duty schedule (1.4% / 2.4% / 6.0% / 5.5% / 6.5% marginal tiers; the $960K–$2M band is modelled as marginal 5.5%, matching the SRO flat-slab rule exactly at $2M).",
    sellerAgentCommission:
      "Ratemyagent / OpenAgent 2025 Victoria agent-commission surveys — statewide median ~2.0% ex-GST, ~2.2% GST-inclusive; Melbourne metro typical 1.6–2.5%.",
  },
};

/** All modelled jurisdictions, in a stable order. */
export const JURISDICTIONS: readonly Jurisdiction[] = [
  US_NATIONAL,
  CANADA_ONTARIO,
  AUSTRALIA_NSW,
  AUSTRALIA_VIC,
];

/** Look up a jurisdiction by its stable id. Returns `undefined` if unknown. */
export function getJurisdiction(id: string): Jurisdiction | undefined {
  return JURISDICTIONS.find((j) => j.id === id);
}
